// Dashboard types
export interface DashboardStats {
  totalTrips: number
  upcomingTrips: number
  activeTrips: number
  completedTrips: number
  daysOnTrail: number
  crewMembers: number
  totalWaypoints: number
  totalPhotos: number
  vehicleCount: number
}

export interface DashboardTrip {
  id: string
  name: string
  description: string | null
  start_date: string | null
  end_date: string | null
  status: 'upcoming' | 'active' | 'completed' | 'draft'
  member_count: number
  waypoint_count: number
  photo_count: number
  cover_photo_url?: string | null
  created_at: string
  updated_at: string
  weather?: WeatherData
  actionItemsCount?: number
  tripReadiness?: number
}

export interface DashboardData {
  stats: DashboardStats
  recentTrips: DashboardTrip[]
  upcomingTrips: DashboardTrip[]
}

// API response types - MVP version
export interface CreateTripDto {
  name: string
  description?: string
  start_date?: string
  end_date?: string
  is_public?: boolean
  meetup_location?: string
  meetup_time?: string
  radio_channel?: string
  radio_frequency?: string
  radio_type?: string
  crew_limit?: number | null
}

export interface UpdateTripDto extends Partial<CreateTripDto> {}

export interface CreateQuickTripDto {
  name: string
  campspot_latitude: number
  campspot_longitude: number
  campspot_name?: string
  meetup_latitude?: number
  meetup_longitude?: number
  meetup_location?: string
  meal_notes?: string
  start_date?: string
  end_date?: string
  description?: string
  /** Present when this quick trip was created by accepting a weekend-suggestion feed card. */
  suggestion_id?: string
}

// Waypoint DTOs moved to routes module to avoid conflicts

export interface AddMemberDto {
  email: string
  role?: 'owner' | 'member'
  member_type?: 'full_member' | 'guest'
  personal_message?: string
}

export interface UpdateMemberRoleDto {
  role: 'owner' | 'member'
}

export interface CreateInvitationDto {
  email: string
  role?: 'owner' | 'member'
  member_type?: 'full_member' | 'guest'
  personal_message?: string
}

export interface AcceptInvitationDto {
  invitation_token: string
}

export interface TripQueryDto {
  page?: number
  limit?: number
  search?: string
  is_public?: boolean
}

// Response types - MVP version
export interface TripResponse {
  id: string
  name: string
  description: string | null
  start_date: string | null
  end_date: string | null
  is_public: boolean
  meetup_location: string | null
  archived_at: string | null
  /** Set when trip has ended (see backend retention job) */
  completed_at?: string | null
  /** Recovery window before permanent deletion */
  retention_window_started_at?: string | null
  meetup_time: string | null
  radio_channel?: string | null
  radio_frequency?: string | null
  radio_type?: string | null
  creation_source?: 'manual' | 'quick'
  created_by: string
  created_at: string
  crew_limit?: number | null
  updated_at: string
  // Optional fields that backend may inject for list endpoints
  member_count?: number
  waypoint_count?: number
  cover_photo_url?: string | null
  cover_photo_id?: string | null
}

// Import WaypointResponse from routes module
import type { WaypointResponse } from './routes'
import type { MealResponseDto } from '@/shared/types/meals'
import type { GearResponseDto } from '@/modules/gear'

/** Modification summary shown for a crew member's primary vehicle (read-only in trip context) */
export interface MemberPrimaryVehicleModification {
  id: string
  category: string
  product_name: string
  product_model?: string | null
  brand_name?: string | null
  custom_brand_name?: string | null
  description?: string | null
  purchase_price?: number | null
  logo_url?: string | null
}

export interface AddTagAlongDto {
  guest_name: string
}

export interface MemberResponse {
  id: string
  trip_id: string
  user_id: string | null
  role: 'owner' | 'member'
  member_type: 'full_member' | 'guest'
  military_role?: string | null
  parent_member_id?: string | null
  tag_alongs?: MemberResponse[]
  invitation_id?: string
  guest_name?: string
  departure_time?: string
  departure_location?: string
  joined_at: string
  user?: {
    id: string
    email: string
    user_metadata?: Record<string, any>
  }
  primary_vehicle?: {
    id: string
    make: string
    model: string
    year?: number | null
    vehicle_type?: string
    is_daily_driver?: boolean
    has_winch?: boolean | null
    has_skid_plates?: boolean | null
    has_rock_sliders?: boolean | null
    has_roof_rack?: boolean | null
    has_roof_top_tent?: boolean | null
    has_truck_camper?: boolean | null
    modifications?: MemberPrimaryVehicleModification[]
  } | null
  user_profile?: {
    profile_image_path?: string | null
    banner_image_path?: string | null
    bio?: string | null
    location?: string | null
  } | null
}

export interface InvitationResponse {
  id: string
  trip_id: string
  email: string
  role: 'owner' | 'member'
  member_type: 'full_member' | 'guest'
  invited_by: string
  personal_message?: string
  invitation_token: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  expires_at: string
  accepted_at?: string
  declined_at?: string
  created_at: string
  updated_at: string
  trip?: {
    id: string
    name: string
    description?: string
    start_date?: string
    end_date?: string
  }
  inviter?: {
    id: string
    email: string
    user_metadata?: Record<string, any>
  }
}

export interface TripWithDetailsResponse extends TripResponse {
  members: MemberResponse[]
  waypoints: WaypointResponse[]
  announcements?: TripAnnouncement[]
  invitations?: InvitationResponse[]
  meals?: MealResponseDto[]
  gear?: GearResponseDto[]
  creator: {
    id: string
    email: string
    user_metadata?: Record<string, any>
  }
  hasAccess?: boolean
  guestPermissions?: GuestPermissions
  /** Set when the current user has a pending trip invite (can preview and accept) */
  pending_invite?: { id: string } | null
  /** Set when access was granted via admin bypass (read-only; admin is not a member) */
  is_admin_view?: boolean
}

// API error response class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }
  }
}

// Generic API response wrapper
export interface ApiResponse<T> {
  data: T
  message?: string
  status: number
}

// Pagination response
export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// Weather Data
export interface WeatherData {
  temp: number
  feels_like: number
  condition: string
  description: string
  icon: string
  humidity: number
  wind_speed: number
}

// Trip Weather Location
export interface TripWeatherLocation {
  id: string
  name: string
  latitude: number
  longitude: number
  poi_type: 'campsite' | 'campground' | 'meetup_spot'
  weather: WeatherData | null
}

// Campspotter normalized assessment (for manual fetch display)
export interface CampspotterAssessment {
  waypointId: string
  spotName: string
  nearestCity?: string
  startDate: string
  endDate: string
  status: string
  overallScore: number
  minScore: number
  maxScore: number
  penalties: Array<{
    condition: string
    explanation: string
    streakHours: number
    penaltyPoints: number
  }>
  streaksDetected?: Record<string, { hours: number; periods?: number }>
  dailyAssessments: Array<{
    date: string
    dayScore?: number
    periods: Array<{
      timestamp: string
      weatherMain?: string | null
      weatherDescription?: string | null
      temperatureF: number
      feelsLikeF: number
      rainMm?: number
      snowMm?: number
      precipitationProbability?: number
      windSpeed?: number
      windGust?: number
      clouds?: number
      uvIndex?: number
      periodScore?: number
      emergencyRiskLevel?: string
    }>
  }>
}

// Action Items
export interface ActionItem {
  id: string
  trip_id: string
  title: string
  description?: string | null
  item_type: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  assigned_to?: string | null
  due_date?: string | null
  created_by: string
  completed_at?: string | null
  created_at: string
  updated_at: string
}

export interface ActionItemResponse extends ActionItem {
  trip?: {
    id: string
    name: string
  }
}

export interface CreateActionItemDto {
  trip_id: string
  title: string
  description?: string
  item_type?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to?: string
  due_date?: string
}

// Guest Permissions Types
export interface GuestPermissions {
  id: string
  trip_id: string
  allow_radio_channel: boolean
  allow_meetup_location: boolean
  allow_crew: boolean
  allow_meals: boolean
  allow_end_location: boolean
  created_at: string
  updated_at: string
}

export interface UpdateGuestPermissionsDto {
  allow_radio_channel?: boolean
  allow_meetup_location?: boolean
  allow_crew?: boolean
  allow_meals?: boolean
  allow_end_location?: boolean
}

// Guest Access Request Types
export interface GuestAccessRequest {
  id: string
  trip_id: string
  guest_member_id: string
  requested_at: string
  status: 'pending' | 'approved' | 'denied'
  responded_at?: string | null
  responded_by?: string | null
  trip_members?: {
    guest_name?: string | null
    guest_email?: string | null
  }
}

export interface CreateAccessRequestDto {
  accessToken: string
}

export interface RespondToAccessRequestDto {
  approved: boolean
}

export interface UpdateActionItemDto {
  title?: string
  description?: string
  item_type?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  assigned_to?: string
  due_date?: string
}

// Trip Announcements (bulletin board)
export interface TripAnnouncement {
  id: string
  trip_id: string
  author_id: string | null
  content: string
  created_at: string
  updated_at: string
}

export interface CreateAnnouncementDto {
  content: string
}

export interface UpdateAnnouncementDto {
  content?: string
}

export type TripFeedPostType = 'message' | 'offer' | 'need'
export type TripFeedReactionType = 'thumbs_up' | 'check'

export interface TripFeedReaction {
  id: string
  post_id: string
  user_id: string
  reaction_type: TripFeedReactionType
  created_at: string
}

export interface TripFeedMention {
  id: string
  post_id: string
  mentioned_user_id: string
  created_at: string
}

export interface TripFeedPost {
  id: string
  trip_id: string
  author_id: string
  post_type: TripFeedPostType
  content: string
  is_pinned: boolean
  is_resolved: boolean
  resolved_at: string | null
  resolved_by: string | null
  edited_at: string | null
  deleted_at: string | null
  created_at: string
  updated_at: string
  reactions: TripFeedReaction[]
  mentions: TripFeedMention[]
}

export interface CreateTripFeedPostDto {
  content: string
  postType: TripFeedPostType
  mentionUserIds?: string[]
}

// ─── Crew Chat ───────────────────────────────────────────────────────────────

export interface TripCrewMessage {
  id: string
  trip_id: string
  author_id: string
  parent_id: string | null
  body: string
  edited_at: string | null
  deleted_at: string | null
  created_at: string
  updated_at: string
}

export interface TripChatReadState {
  trip_id: string
  user_id: string
  last_read_at: string
  updated_at: string
}

export interface CreateTripCrewMessageDto {
  body: string
  parentId?: string
}

export interface UpdateTripCrewMessageDto {
  body: string
}

export interface ListTripCrewMessagesParams {
  limit?: number
  before?: string
  parentId?: string
}
